import { FilterMatchMode } from 'primereact/api';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useRef } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    XStack,
    YStack,
} from 'smoothie';

import { MonospaceDetailText } from '../../../common/components/GridCellRenderers';
import {
    formatToSimpleDateTimeString,
    getPersonDisplayName,
} from '../../../common/utils/formatting';
import type { MemberDetails, UserEvent } from '../../entityTypes';
import { getTimestampedCsvFileName } from '../utils/exportMembersCsv';

interface MembershipUserEventsTabPanelProps {
    members: MemberDetails[];
}

interface UserEventRow extends UserEvent {
    rowId: string;
    memberEmail: string;
    memberName: string;
}

const getUserEventRows = (members: MemberDetails[]): UserEventRow[] => {
    return members
        .flatMap((member) =>
            (member.events ?? []).map((event, index) => ({
                ...event,
                rowId: `${member.id}-${event.id}-${index}`,
                memberEmail: member.email,
                memberName: getPersonDisplayName(member, member.email),
            })),
        )
        .sort(
            (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
        );
};

const memberRenderer = ({ memberEmail, memberName }: UserEventRow) => (
    <YStack>
        <BodyMediumRegular>{memberName}</BodyMediumRegular>
        <MonospaceDetailText>{memberEmail}</MonospaceDetailText>
    </YStack>
);

export const MembershipUserEventsTabPanel = ({
    members,
}: MembershipUserEventsTabPanelProps) => {
    const dataTableRef = useRef<DataTable<UserEventRow[]>>(null);
    const rows = getUserEventRows(members);

    const exportCSV = () => {
        dataTableRef.current?.exportCSV();
    };

    return (
        <YStack gap={12} width="100%">
            {rows.length > 0 && (
                <XStack justifyContent="flex-end" width="100%">
                    <Button
                        text="Export to CSV"
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.SMALL}
                        onPress={exportCSV}
                        width="auto"
                    />
                </XStack>
            )}
            {rows.length === 0 ? (
                <BodyMediumRegular fontStyle="italic">
                    No user events
                </BodyMediumRegular>
            ) : (
                <DataTable
                    ref={dataTableRef}
                    value={rows}
                    dataKey="rowId"
                    size="small"
                    tableStyle={{ width: '100%' }}
                    sortField="createdAt"
                    sortOrder={-1}
                    scrollable
                    scrollHeight="70vh"
                    emptyMessage="No user events"
                    exportFilename={getTimestampedCsvFileName(
                        'user-events',
                    ).replace(/\.csv$/, '')}
                    filters={{
                        memberName: {
                            value: null,
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        eventType: {
                            value: null,
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                    }}
                >
                    <Column
                        field="memberName"
                        header="Member"
                        body={memberRenderer}
                        style={{ minWidth: 220 }}
                        filter
                    />
                    <Column field="memberEmail" header="Member Email" hidden />
                    <Column
                        field="eventType"
                        header="Event Type"
                        body={({ eventType }: UserEventRow) => (
                            <BodySmallRegular>{eventType}</BodySmallRegular>
                        )}
                        sortable
                        style={{ minWidth: 260 }}
                        filter
                    />
                    <Column
                        field="ipAddress"
                        header="IP Address"
                        body={({ ipAddress }: UserEventRow) => (
                            <MonospaceDetailText>
                                {ipAddress ?? '-'}
                            </MonospaceDetailText>
                        )}
                        style={{ minWidth: 160 }}
                    />
                    <Column
                        field="userAgent"
                        header="User Agent"
                        body={({ userAgent }: UserEventRow) => (
                            <BodySmallRegular>
                                {userAgent ?? '-'}
                            </BodySmallRegular>
                        )}
                        style={{ minWidth: 320 }}
                    />
                    <Column
                        field="createdAt"
                        header="Created At"
                        body={({ createdAt }: UserEventRow) =>
                            formatToSimpleDateTimeString(createdAt)
                        }
                        sortable
                        style={{ minWidth: 180 }}
                    />
                    <Column
                        field="updatedAt"
                        header="Updated At"
                        body={({ updatedAt }: UserEventRow) =>
                            formatToSimpleDateTimeString(updatedAt)
                        }
                        sortable
                        style={{ minWidth: 180 }}
                    />
                </DataTable>
            )}
        </YStack>
    );
};
